import React, { useState, useEffect } from 'react';
import { 
  Loader, 
  Progress, 
  Alert, 
  Button, 
  Group, 
  Text, 
  Paper, 
  Stack,
  Notification
} from '@mantine/core';
import { 
  IconWifi, 
  IconWifiOff, 
  IconRefresh, 
  IconCloudOff,
  IconCheck
} from '@tabler/icons-react';
import { 
  LOADING_CHECK_INTERVAL, 
  RETRY_CONFIG,
  NOTIFICATION_AUTO_CLOSE 
} from '../config/constants';

/**
 * 全域載入指示器（簡化版）
 */
export function GlobalLoadingIndicator() {
  const [isVisible, setIsVisible] = useState(false);

  // 監聽全域載入狀態
  useEffect(() => {
    const checkLoading = () => {
      // 簡單檢查是否有任何載入狀態
      const hasLoading = document.querySelector('[data-loading="true"]');
      setIsVisible(!!hasLoading);
    };

    // 初始檢查
    checkLoading();
    
    const interval = setInterval(checkLoading, LOADING_CHECK_INTERVAL);
    
    return () => clearInterval(interval);
  }, []);

  if (!isVisible) {return null;}

  return (
    <Paper
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 10000,
        padding: '12px 16px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Group>
        <Loader size="sm" />
        <Text size="sm">
          處理中...
        </Text>
      </Group>
    </Paper>
  );
}

/**
 * 網路狀態指示器
 */
export function NetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineAlert(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline && !showOfflineAlert) {return null;}

  return (
    <>
      {showOfflineAlert && (
        <Alert
          icon={<IconWifiOff size={16} />}
          title="網路連線中斷"
          color="red"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            maxWidth: '90vw',
            width: '400px'
          }}
          withCloseButton
          onClose={() => setShowOfflineAlert(false)}
        >
          <Text size="sm" mb="sm">
            您的網路連線已中斷。部分功能可能無法正常使用。
          </Text>
          <Button 
            size="xs" 
            leftSection={<IconRefresh size={14} />}
            onClick={() => window.location.reload()}
          >
            重新載入
          </Button>
        </Alert>
      )}
      
      {isOnline && (
        <Notification
          icon={<IconWifi size={16} />}
          color="green"
          title="網路已恢復"
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 10001
          }}
          autoClose={NOTIFICATION_AUTO_CLOSE.SUCCESS}
        >
          網路連線已恢復正常
        </Notification>
      )}
    </>
  );
}

/**
 * 智能重試組件
 */
export function SmartRetry({ 
  onRetry, 
  error, 
  maxRetries = RETRY_CONFIG.MAX_RETRIES, 
  retryDelay = RETRY_CONFIG.DELAY_MS,
  children 
}) {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastError, setLastError] = useState(null);

  const handleRetry = async () => {
    if (retryCount >= maxRetries) {
      return;
    }

    setIsRetrying(true);
    setRetryCount(prev => prev + 1);

    try {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      await onRetry();
      setLastError(null);
      setRetryCount(0);
    } catch (err) {
      setLastError(err);
    } finally {
      setIsRetrying(false);
    }
  };

  if (!error && !lastError) {
    return children;
  }

  const currentError = error || lastError;
  const canRetry = retryCount < maxRetries;

  return (
    <Alert
      icon={<IconCloudOff size={16} />}
      title="操作失敗"
      color="red"
      style={{ margin: '20px' }}
    >
      <Stack gap="sm">
        <Text size="sm">
          {currentError?.message || '發生未知錯誤'}
        </Text>
        
        {canRetry && (
          <Group>
            <Button 
              size="sm" 
              leftSection={<IconRefresh size={14} />}
              loading={isRetrying}
              onClick={handleRetry}
            >
              重試 ({retryCount}/{maxRetries})
            </Button>
            <Text size="xs" c="dimmed">
              {isRetrying && `重試中... (${retryDelay / 1000}秒後)`}
            </Text>
          </Group>
        )}
        
        {!canRetry && (
          <Alert color="orange" variant="light">
            <Text size="sm">
              已達到最大重試次數。請檢查網路連線或聯繫系統管理員。
            </Text>
          </Alert>
        )}
      </Stack>
    </Alert>
  );
}

/**
 * 進度條指示器
 */
export function ProgressIndicator({ 
  current, 
  total, 
  label = '處理中', 
  showPercentage = true 
}) {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <Paper p="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={500}>{label}</Text>
          {showPercentage && (
            <Text size="sm" c="dimmed">{percentage}%</Text>
          )}
        </Group>
        <Progress 
          value={percentage} 
          animated={current < total}
          color={percentage === 100 ? 'green' : 'blue'}
        />
        <Text size="xs" c="dimmed" ta="center">
          {current} / {total}
        </Text>
      </Stack>
    </Paper>
  );
}

/**
 * 無障礙性增強組件
 */
export function AccessibilityEnhancer({ children }) {
  useEffect(() => {
    // 為沒有 aria-label 的按鈕添加標籤
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach(button => {
      const text = button.textContent?.trim();
      if (text) {
        button.setAttribute('aria-label', text);
      }
    });

    // 為表單輸入添加必要的屬性
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      if (!input.getAttribute('aria-describedby') && !input.getAttribute('aria-label')) {
        const label = input.previousElementSibling?.textContent?.trim();
        if (label) {
          input.setAttribute('aria-label', label);
        }
      }
    });

    // 改善對比度（為低對比度元素添加 CSS 類別）
    const lowContrastElements = document.querySelectorAll('[style*="color: gray"], [style*="color: lightgray"]');
    lowContrastElements.forEach(element => {
      element.classList.add('high-contrast');
    });
  }, []);

  return <>{children}</>;
}

/**
 * 鍵盤導航增強
 */
export function KeyboardNavigation() {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Alt + 數字鍵：快速導航到對應的選單項目
      if (event.altKey && event.key >= '1' && event.key <= '9') {
        event.preventDefault();
        const menuIndex = parseInt(event.key) - 1;
        const menuItems = document.querySelectorAll('[data-menu-item]');
        if (menuItems[menuIndex]) {
          menuItems[menuIndex].click();
        }
      }

      // Ctrl + Enter：提交表單
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        const submitButton = document.querySelector('button[type="submit"], button:contains("提交"), button:contains("儲存"), button:contains("確定")');
        if (submitButton && !submitButton.disabled) {
          submitButton.click();
        }
      }

      // Escape：關閉模態框
      if (event.key === 'Escape') {
        const closeButtons = document.querySelectorAll('[data-close], .mantine-Modal-close, .ant-modal-close');
        if (closeButtons.length > 0) {
          closeButtons[closeButtons.length - 1].click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return null;
}

/**
 * 成功操作反饋
 */
export function SuccessFeedback({ visible, message, onClose, autoClose = NOTIFICATION_AUTO_CLOSE.SUCCESS }) {
  useEffect(() => {
    if (visible && autoClose > 0) {
      const timer = setTimeout(onClose, autoClose);
      return () => clearTimeout(timer);
    }
  }, [visible, autoClose, onClose]);

  if (!visible) {return null;}

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10002,
        animation: 'fadeInScale 0.3s ease-out'
      }}
    >
      <Paper 
        p="xl" 
        shadow="xl" 
        radius="lg"
        style={{
          textAlign: 'center',
          minWidth: '300px',
          backgroundColor: 'var(--mantine-color-green-0)',
          border: '2px solid var(--mantine-color-green-5)'
        }}
      >
        <Stack align="center" gap="md">
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-green-5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'bounceIn 0.6s ease-out'
            }}
          >
            <IconCheck size={30} color="white" />
          </div>
          <Text size="lg" fw={600} c="green">
            {message || '操作成功！'}
          </Text>
        </Stack>
      </Paper>
    </div>
  );
}

const UXEnhancements = {
  GlobalLoadingIndicator,
  NetworkStatus,
  SmartRetry,
  ProgressIndicator,
  AccessibilityEnhancer,
  KeyboardNavigation,
  SuccessFeedback
};

export default UXEnhancements;
